import * as React from "react";
import { PaletteMode } from "@mui/material";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const isMobile = window.innerWidth <= 768;
const logoStyle = {
  width: isMobile ? "90px" : "140px",
  height: "auto",
  cursor: "pointer",
};

interface AppAppBarProps {
  mode: PaletteMode;
  toggleColorMode: () => void;
  isBannerVisible?: boolean;
  showProfile?: boolean; // New prop to control profile visibility
  useTextLogo?: boolean; // New prop to toggle between Text and Image logo
  simpleNavbar?: boolean; // New prop to hide desktop links and show hamburger always
  transparentMode?: boolean; // New prop for transparent background without pill shape
  showLogin?: boolean; // New prop to control login button visibility (default true)
}

function AppAppBar({ mode, toggleColorMode, isBannerVisible, showProfile = false, useTextLogo = false, simpleNavbar = false, transparentMode = false, showLogin = true }: AppAppBarProps) {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const sectionElement = document.getElementById(sectionId);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth" });
      setOpen(false);
    }
  };

  return (
    <div>
      <AppBar
        position="fixed"
        sx={{
          boxShadow: transparentMode && !scrolled ? "none" : "0 4px 20px rgba(0,0,0,0.1)",
          bgcolor: transparentMode && !scrolled ? "transparent" : "rgba(255, 255, 255, 0.8)",
          backdropFilter: transparentMode && !scrolled ? "none" : "blur(12px)",
          backgroundImage: "none",
          border: transparentMode && !scrolled ? "none" : "1px solid rgba(255, 255, 255, 0.5)",
          transition: "background-color 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease, border 0.3s ease",

          // Static Floating Pill Styles
          width: "min(95%, 1200px)",
          left: "50%",
          transform: "translateX(-50%)",
          top: "16px",
          borderRadius: "50px",
          padding: "0",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            variant="regular"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
              minHeight: "60px",
              px: "16px !important",
            }}
          >
            {/* Logo Logic */}
            {useTextLogo ? (
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => window.location.href = "/"}>
                <Typography variant="h5" sx={{ color: '#3F51B5', fontWeight: 800, fontFamily: "General-Sans-bold, sans-serif" }}>
                  App<span style={{ color: '#FFC107' }}>20</span>
                </Typography>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '2px', marginBottom: '14px' }}>
                  <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#3F51B5" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
            ) : (
              <img
                src="https://github.com/appversal/filehost/blob/main/download.png?raw=true"
                style={logoStyle}
                alt="logo of App20"
                onClick={() => window.location.href = "/"}
                data-track="image"
                data-name="app20_header_logo"
              />
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

              {/* Profile Icon - Visible if showProfile is true */}
              {/* Profile Icon - Visible if showProfile is true */}
              {/* {simpleNavbar && showLogin && (
                     <Button
                        onClick={() => window.location.href = "/ios/login"}
                        sx={{ 
                            display: "flex", 
                            color: "#3F51B5", 
                            fontWeight: 700, 
                            textTransform: "none",
                            fontSize: "18px",
                            minWidth: "auto",
                            p: 1,
                            mr: 0.5
                        }}
                     >
                        Login
                     </Button>
                )} */}

              {showProfile && (
                <Button
                  onClick={() => window.location.href = "/profile"}
                  sx={{ minWidth: "auto", p: 1 }}
                >
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Head circle */}
                    <circle cx="14" cy="9" r="5" stroke="#3F51B5" strokeWidth="3" fill="none" />
                    {/* Body arc */}
                    <path d="M4 24C4 18.5 8.5 14 14 14C19.5 14 24 18.5 24 24" stroke="#3F51B5" strokeWidth="3" strokeLinecap="round" fill="none" />
                  </svg>
                </Button>
              )}

              {/* Mobile Menu Icon or All Screens if Simple Navbar */}
              <Box sx={{ display: { xs: "flex", md: simpleNavbar ? "flex" : "none" } }}>
                <Button
                  onClick={toggleDrawer(true)}
                  sx={{ minWidth: "auto", p: 1 }}
                >
                  <svg width="28" height="18" viewBox="0 0 28 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="28" height="3" rx="1.5" fill="#3F51B5" />
                    <rect y="7.5" width="28" height="3" rx="1.5" fill="#3F51B5" />
                    <rect x="8" y="15" width="20" height="3" rx="1.5" fill="#3F51B5" />
                  </svg>
                </Button>
              </Box>
            </Box>

            {/* Desktop Menu - Hide if simpleNavbar is true */}
            {!simpleNavbar && (
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 2 }}>
                <MenuItem onClick={() => scrollToSection("testimonials")}>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>Testimonials</Typography>
                </MenuItem>
                <MenuItem onClick={() => scrollToSection("offers-section")}>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>Offers</Typography>
                </MenuItem>
                {/* {showLogin && (
                   <MenuItem onClick={() => window.location.href = "/ios/login"}>
                      <Typography variant="body2" color="text.primary" fontWeight={600}>Login</Typography>
                   </MenuItem>
               )} */}
                <Button
                  variant="contained"
                  onClick={() => window.location.href = "https://offer.app20.co/apk"}
                  sx={{
                    backgroundColor: "#3341FC",
                    color: "#fff",
                    borderRadius: "20px",
                    textTransform: "none",
                    ml: 2,
                    boxShadow: "0 2px 8px rgba(51, 65, 252, 0.3)",
                    "&:hover": { backgroundColor: "#0029b3" }
                  }}
                  startIcon={<FileDownloadOutlinedIcon />}
                >
                  Download App
                </Button>
              </Box>
            )}

            <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
              <Box sx={{ minWidth: "60vw", p: 2, backgroundColor: "#fff", height: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <Button onClick={toggleDrawer(false)} sx={{ minWidth: "auto" }}>
                    <Typography variant="h6" sx={{ color: "#000" }}>✕</Typography>
                  </Button>
                </Box>

                {showProfile && (
                  <MenuItem onClick={() => window.location.href = "/profile"}>
                    <Typography variant="body1" color="text.primary">Profile</Typography>
                  </MenuItem>
                )}

                <MenuItem onClick={() => scrollToSection("testimonials")}>
                  <Typography variant="body1" color="text.primary">Testimonials</Typography>
                </MenuItem>
                <MenuItem onClick={() => scrollToSection("offers-section")}>
                  <Typography variant="body1" color="text.primary">Offers</Typography>
                </MenuItem>
                {/* {showLogin && (
                        <MenuItem onClick={() => window.location.href = "/login"}>
                            <Typography variant="body1" color="text.primary">Login</Typography>
                        </MenuItem>
                    )} */}

              </Box>
            </Drawer>

          </Toolbar>
        </Container>
      </AppBar>
    </div>
  );
}

export default AppAppBar;
